/**
 * Parts Master export hook — pulls every part matching the current filters
 * (paginating through the API since `LargeResultsSetPagination` caps each
 * page at 500) and writes an .xlsx file via SheetJS.
 *
 * Only currently-visible columns are included so the exported sheet matches
 * what the user is looking at. Numeric columns are written as numbers (not
 * strings) so Excel sums/filters work out of the box.
 */
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { partsAPI } from '../../lib/api/parts';
import type { Part } from '../../lib/api/types/parts';
import { EXTRA_COLUMNS } from '../../config/parts-master-columns';
import {
  UNIVERSAL_COLUMNS,
  SCHEDULE_COLUMNS,
  type PartsMasterColumnDescriptor,
} from './usePartsMasterVisibleColumns';

/** Page size used while paging through the API. Matches the backend cap. */
const EXPORT_PAGE_SIZE = 500;
/** Hard upper bound to keep the browser sane on enormous tenants. */
const EXPORT_MAX_ROWS = 100000;

interface UsePartsExportArgs {
  /** Query params shared by the list view (search, filters, view-context). */
  buildBaseParams: () => Record<string, string | number | boolean | undefined>;
  /** Which columns are visible right now — drives sheet shape. */
  visibleIds: Set<string>;
  /** Whether schedule columns are shown for this tenant. */
  showSchedule: boolean;
  /** Whether the EXTRA_COLUMNS registry is shown for this tenant. */
  showExtras: boolean;
}

interface ExportColumn {
  id: string;
  label: string;
  /** Pull a row's typed value out of `Part`. */
  read: (row: Part) => unknown;
  /** Coerce the value into a final cell payload. Numbers stay numeric. */
  toCell: (value: unknown) => string | number | null;
}

/** Coerce decimals stored as strings ("1.5") into numbers; leave nulls alone. */
function asNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const num = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(num) ? num : null;
}

function asStringOrNull(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  return String(value);
}

/**
 * Build the column descriptor list used by the export.
 *
 * Order: universal -> schedule -> extras. Visibility set filters out the
 * ones the user has hidden. Returned in render order so the sheet column
 * sequence matches what the user sees on-screen.
 */
function resolveExportColumns(
  visibleIds: Set<string>,
  showSchedule: boolean,
  showExtras: boolean,
): ExportColumn[] {
  const out: ExportColumn[] = [];

  const stringCol = (descriptor: PartsMasterColumnDescriptor): ExportColumn => ({
    id: descriptor.id,
    label: descriptor.label,
    read: (row: Part) => (row as unknown as Record<string, unknown>)[descriptor.id],
    toCell: asStringOrNull,
  });

  for (const col of UNIVERSAL_COLUMNS) {
    if (!visibleIds.has(col.id)) continue;
    if (col.id === 'is_active') {
      out.push({
        id: col.id,
        label: col.label,
        read: (row: Part) => row.is_active,
        toCell: (v: unknown) => (v === true ? 'Active' : v === false ? 'Inactive' : null),
      });
    } else {
      out.push(stringCol(col));
    }
  }

  if (showSchedule) {
    for (const col of SCHEDULE_COLUMNS) {
      if (!visibleIds.has(col.id)) continue;
      if (col.id === 'schedule_firm') {
        out.push({
          id: col.id,
          label: col.label,
          read: (row: Part) => row.schedule_firm,
          toCell: asNumberOrNull,
        });
      } else if (col.id === 'schedule_active') {
        out.push({
          id: col.id,
          label: col.label,
          read: (row: Part) => row.schedule_active,
          toCell: (v: unknown) =>
            v === true ? 'Active' : v === false ? 'Inactive' : null,
        });
      }
    }
  }

  if (showExtras) {
    for (const col of EXTRA_COLUMNS) {
      const id = col.key as string;
      if (!visibleIds.has(id)) continue;
      out.push({
        id,
        label: col.label,
        read: (row: Part) => (row as unknown as Record<string, unknown>)[id],
        toCell: (value: unknown) => {
          if (col.numeric) return asNumberOrNull(value);
          if (col.format) {
            const formatted = col.format(value);
            // Don't write "—" placeholders — leave the Excel cell blank.
            if (formatted === '' || formatted === '—') return null;
            return formatted;
          }
          return asStringOrNull(value);
        },
      });
    }
  }

  return out;
}

interface UsePartsExportResult {
  exporting: boolean;
  exportToXlsx: () => Promise<void>;
}

/**
 * Hook that returns an export handler and a loading flag for the toolbar
 * button.
 *
 * @returns `exporting` flag + `exportToXlsx` action.
 */
export function usePartsExport({
  buildBaseParams,
  visibleIds,
  showSchedule,
  showExtras,
}: UsePartsExportArgs): UsePartsExportResult {
  const [exporting, setExporting] = useState(false);

  const exportToXlsx = useCallback(async () => {
    setExporting(true);
    try {
      const exportColumns = resolveExportColumns(visibleIds, showSchedule, showExtras);
      if (exportColumns.length === 0) {
        toast.error('No columns selected — nothing to export.');
        return;
      }

      // Page through the API. We drive the loop off the response's `count`
      // field rather than `results.length < EXPORT_PAGE_SIZE` because the
      // latter masks any backend-side page-size cap (e.g. if the request is
      // silently downgraded to the default 100/page, the old heuristic would
      // exit after page 1 and silently truncate the export). Counting rows
      // against the reported `count` is robust to that.
      const baseParams = buildBaseParams();
      const allRows: Part[] = [];
      let page = 1;
      let truncated = false;
      let expectedTotal = Infinity;

      // Strip pagination keys the caller may already have set — we manage them.
      const cleanedBase: Record<string, string | number | boolean | undefined> = {
        ...baseParams,
      };
      delete cleanedBase['page'];
      delete cleanedBase['page_size'];

      while (allRows.length < EXPORT_MAX_ROWS) {
        const params: Record<string, string | number | boolean | undefined> = {
          ...cleanedBase,
          page,
          page_size: EXPORT_PAGE_SIZE,
        };
        const response = await partsAPI.list(params);
        const results = response.data.results ?? [];
        allRows.push(...results);
        // Cache the total once — `count` is invariant across pages.
        if (page === 1) expectedTotal = response.data.count ?? results.length;
        // Stop conditions:
        //  - we've fetched everything the API claims to have (count reached)
        //  - the API returned an empty page (defensive — shouldn't happen
        //    before count, but protects against stuck loops on backend bugs)
        if (results.length === 0) break;
        if (allRows.length >= expectedTotal) break;
        page += 1;
      }

      if (allRows.length >= EXPORT_MAX_ROWS && expectedTotal > EXPORT_MAX_ROWS) {
        truncated = true;
      }

      if (allRows.length === 0) {
        toast.error('No parts match the current filters.');
        return;
      }

      // Build sheet: header row + one row per part.
      const header: string[] = exportColumns.map(c => c.label);
      const data: (string | number | null)[][] = [header];
      for (const part of allRows) {
        const cells: (string | number | null)[] = exportColumns.map(col =>
          col.toCell(col.read(part)),
        );
        data.push(cells);
      }

      const worksheet = XLSX.utils.aoa_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Parts Master');

      const today = new Date().toISOString().split('T')[0];
      const filename = `parts-master-${today}.xlsx`;
      XLSX.writeFile(workbook, filename);

      if (truncated) {
        toast.success(
          `Exported first ${EXPORT_MAX_ROWS.toLocaleString()} parts (limit reached).`,
        );
      } else {
        toast.success(`Exported ${allRows.length.toLocaleString()} parts.`);
      }
    } catch (err: unknown) {
      console.error('Parts Master export failed:', err);
      const message =
        err instanceof Error ? err.message : 'Export failed — check the console.';
      toast.error(message);
    } finally {
      setExporting(false);
    }
  }, [buildBaseParams, visibleIds, showSchedule, showExtras]);

  return { exporting, exportToXlsx };
}
